import { Eye, Filter, Plus, Search } from "lucide-react";
import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
import { Badge } from "../../../Component/AdminComponents/Badge";
import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import BlueButton from "../../../Component/Common/BlueButton";
import RefreshButton from "../../../Component/Common/RefreshButton";
import UpdateRetailerForm from "../Agent/Retailer/UpdateRetailerForm";
import Pagination from "../../../Component/Common/Pagination";
import { BASE_URL } from "../../../config/api";
import RegularLoader from "../../../Component/Loader/RegularLoader";
import SimpleTable from "../../../Component/Common/SimpleTable";
import ViewButton from "../../../Component/Common/ViewButton";
import { CheckCircle, Clock, XCircle } from "lucide-react";

// 👉 STATIC fallback values
const fallbackRetailer = {
  totalSales: 0,
  totalWarrantySold: 0,
  activeWarranties: 0,
  deactiveWarranties: 0,
  pendingTasks: 0,
  assignedAgent: "Not Assigned",
  connectionStatus: "Connected",
  usingProduct: true,
  status: "Active",
};

export default function OperationsView({ entityType = null, entityId = null }) {
  const navigate = useNavigate();
  const location = useLocation();
  const filterStatus = location.state?.filterStatus || null;

  const [retailerList, setRetailerList] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);

  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [selectedRetailer, setSelectedRetailer] = useState(null);
  console.log("selected retailer: ", selectedRetailer)

  const [currentPage, setCurrentPage] = useState(1);
const [totalPages, setTotalPages] = useState(1);

const [activeTab, setActiveTab] = useState("retailers");

const pageSize = 10;



  const openUpdateModal = (retailer) => {
    setSelectedRetailer(retailer);
    setShowUpdateModal(true);
  };

  const closeUpdateModal = () => {
    setShowUpdateModal(false);
    setSelectedRetailer(null);
  };

  // 🔥 Fetch company_id from session
  const getCompanyId = () => {
    try {
      const session = JSON.parse(localStorage.getItem("authUserData"));
      return session?.id || 1;
    } catch {
      return 1;
    }
  };

  // 🔥 Merge API response with fallback fields
  const mappedRetailers = retailerList.map((r) => ({
  ...fallbackRetailer,

  id: r.id,
  name: r.business_name,
  city: r.city,
  company_code:r.company_code,
  state: r.state,
  mobile: r.contact_phone,
  gst:r.gst, 
  assignedAgent: r.agent_id !== null ? "Created By Agent" : "Other",
  created_by_name:r.created_by_name,
  contact_email: r.contact_email,
  status: r.status === 1 ? "Active" : "Deactivated",

  // keep full object
  raw: r,
}));


const paginatedRetailers = mappedRetailers

  const buildRetailerQuery = () => {
  const session = JSON.parse(localStorage.getItem("authUserData") || "{}");
  const role = session?.role;
  const sessionId = session?.id;
  const agent_code = session?.company_code;

  // 🔹 MODE 2: FROM VIEW (props override session)
  if (entityType && entityId) {
    if (entityType === "company" || entityType === 2) {
      return `company_id=${entityId}`;
    }
    if (entityType === "agent" || entityType === 4) {
      return `agent_id=${entityId}`;
    }
  }

  // 🔹 MODE 1: SIMPLE TABLE (session based)
  if (role === 2) return `company_id=${sessionId}`;
  if (role === 4) return `agent_code=${agent_code}`;

  // 🔹 Admin → no filter
  return "";
};

  // 🔥 Fetch all retailers
  const fetchRetailers = async (page = 1) => {
  try {
    setLoading(true);

    const filter = buildRetailerQuery();

    const res = await fetch(
      `${BASE_URL}common/get-users?role=5&page=${page}${filter ? `&${filter}` : ""}`
    );

    const json = await res.json();

    if (json.status) {
      setRetailerList(json.data || []);
      setCurrentPage(json.pagination?.current_page || 1);
      setTotalPages(json.pagination?.last_page || 1);
    }
  } catch (err) {
    console.error("Fetch Retailers Error:", err);
  } finally {
    setLoading(false);
  }
};

  // 🔥 Search API
  const searchRetailers = async (value) => {
  setSearch(value);
  setCurrentPage(1);

  const filter = buildRetailerQuery();

  if (!value.trim()) {
    fetchRetailers(1);
    return;
  }

  try {
    setLoading(true);

    const res = await fetch(
      `${BASE_URL}common/get-users?role=5&search_value=${value}&page=1${filter ? `&${filter}` : ""}`
    );

    const json = await res.json();

    if (json.status) {
      setRetailerList(json.data || []);
      setTotalPages(json.pagination?.last_page || 1);
    }
  } catch (err) {
    console.error("Search API Error:", err);
  } finally {
    setLoading(false);
  }
};


  // 🔥 Load all retailers on mount
  useEffect(() => {
  fetchRetailers(currentPage);
}, [currentPage]);


  // 🔥 Apply KPI filter
  useEffect(() => {
    if (!filterStatus) return;

    if (filterStatus === "Connected") {
      setRetailerList((prev) =>
        prev.filter((r) => r.connectionStatus === "Connected")
      );
    } else {
      fetchRetailers();
    }
  }, [filterStatus]);

  // ⭐⭐⭐ NEW: API for updating retailer status
  const updateRetailerStatusAPI = async (id, newStatus) => {
    try {
      const response = await fetch(
        `${BASE_URL}company/status/${id}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        }
      );

      return await response.json();
    } catch (error) {
      console.error("Status Update API Error:", error);
      return { status: false };
    }
  };

  // ⭐⭐⭐ UPDATED: Toggle status (API + UI)
  const handleToggleRetailerStatus = async (id) => {
    const updated = retailerList.map((r) =>
      r.id === id
        ? { ...r, status: r.status === 1 ? "Deactivated" : "Active" }
        : r
    );

    const original = retailerList;

    // Update UI immediately
    setRetailerList(updated);

    // Determine new status in numeric (1 = active, 0 = deactive)
    const newStatus =
      updated.find((r) => r.id === id).status === "Active" ? 1 : 0;

    const result = await updateRetailerStatusAPI(id, newStatus);

    if (result.status){
      window.location.reload()
    }

    // If API fails → rollback
    if (!result.status) {
      alert("Failed to update status!");
      setRetailerList(original);
    }
  };

  const retailerRows = mappedRetailers.map((r) => ({
  raw: r.raw,
  cells: [
    // Retailer
    {
      title: `${r.name}`,
      sub: (
        <div className="space-y-0.5">
          <div className="text-[11px] text-slate-500">
            ({r.company_code})
            {/* ·  */}
          </div>
          {/* <div className="text-[10px] text-slate-400">
            GSTIN: {r.gst}
          </div> */}
        </div>
      ),
    },

    // Contact
    {
      title: r.mobile || "—",
      sub: <span className="text-[10px] text-slate-500">{r.contact_email}</span>,
    },

    {
      title: r.city || "—",
      sub: <span className="text-[10px] text-slate-500">{r.state}</span>,
    },

    // Agent
    // {
    //   title: r.assignedAgent,
    //   sub: <span className="text-[10px] text-slate-500">Primary owner</span>,
    // },
    {
      title: r.created_by_name,
      // sub: <span className="text-[10px] text-slate-500">Primary owner</span>,
    },
    // Status
    {
      title: (
        <div className="flex gap-1">
          <Badge color={r.status === "Active" ? "green" : "red"}>
            {r.status}
          </Badge>

          {/* <Badge
            color={r.connectionStatus === "Connected" ? "blue" : "yellow"}
          >
            {r.connectionStatus === "Connected"
              ? "Connected to platform"
              : "Not connected"}
          </Badge> */}
        </div>
      ),
    },
  ],
}));


const getRetailerSteps = (r) => {
  const step1 =
    r.is_mail_verified === 1 &&
    r.is_wa_verified === 1;

  const step2 = r.is_payment_success === 1;

  const step3 =
    r.pan_verified === 1 &&
    r.pan_json !== null;

  const step4 =
    r.business_type === "unregistered"
      ? true
      : r.gst_verified === 1 &&
        r.gst_json !== null;

  const step5 =
    r.bank_verified === 1 &&
    r.bank_json !== null;

  const step6 = r.is_verified === 7;

  const step7 = r.esign_verified === 1;

  return [
    { label: "Mail + WhatsApp Verified", done: step1 },
    { label: "Payment Success", done: step2 },
    { label: "PAN Verified", done: step3 },
    { label: "GST Verified", done: step4 },
    { label: "Bank Verified", done: step5 },
    { label: "Admin Verification", done: step6 },
    { label: "E-Sign Completed", done: step7 },
  ];
};

  return (
    <div className="min-h-screen flex flex-col">
      <div className="space-y-4">

        <div className="flex gap-2 mb-3">
          <button
            onClick={() => setActiveTab("retailers")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === "retailers"
                ? "bg-blue-600 text-white"
                : "bg-blue-50 text-blue-700"
            }`}
          >
            Retailers
          </button>

          <button
            onClick={() => setActiveTab("steps")}
            className={`px-4 py-2 rounded-lg text-sm font-semibold ${
              activeTab === "steps"
                ? "bg-blue-600 text-white"
                : "bg-blue-50 text-blue-700"
            }`}
          >
            Steps Progress
          </button>
        </div>


      {activeTab === "retailers" && (
        <>

        <SectionCard
          title={
            filterStatus === "Connected"
              ? "Connected Retailers"
              : "Authorized Retail Partner (ARP)"
          }
          right={
            <div className="flex items-center gap-2 text-[11px]">
              <div className="flex items-center gap-1 px-2 py-1 rounded-xl border border-slate-200 bg-slate-50">
                <Search className="w-3 h-3" />
                <input
                  placeholder="Search retailer..."
                  className="bg-transparent text-[11px] outline-none"
                  value={search}
                  onChange={(e) => searchRetailers(e.target.value)}
                />
              </div>

              {/* <BlueButton
                onClick={() => navigate("/add-entity/retailer")}
                className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: "#0B5ED7" }}
              >
                <Plus size={14} className="mr-1" /> Add
              </BlueButton> */}

              <div className="hidden lg:block">
                <RefreshButton onClick={() => fetchRetailers(currentPage)} />
              </div>
            </div>
          }
        >
          {/* TABLE */}
          <main className="flex-1">

            {loading && <RegularLoader />}


            {!loading && retailerList.length > 0 && (
              <>
                {/* Mobile Cards */}
                <div className="grid gap-3 md:hidden">
                  {paginatedRetailers.map((r) => (
                    <div
                      key={r.id}
                      className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm"
                    >
                      {/* Header */}
                      <div className="flex justify-between items-start">
                        <div>
                          <h3 className="text-sm font-semibold text-slate-800 text-wrap">
                            {r.name}
                          </h3>
                          <p className="text-xs text-slate-500">
                            {r.city}, {r.state}
                          </p>
                        </div>

                        <Badge color={r.status === "Active" ? "green" : "red"}>
                          {r.status}
                        </Badge>
                      </div>

                      {/* Divider */}
                      <div className="border-t my-3"></div>

                      {/* Info */}
                      <div className="space-y-1 text-xs text-slate-600">
                        <div>
                          <span className="font-medium">Mobile:</span> {r.mobile || "—"}
                        </div>
                        <div>
                          <span className="font-medium">Email:</span> {r.contact_email || "—"}
                        </div>
                        <div>
                          <span className="font-medium">Created By:</span> {r.created_by_name}
                        </div>
                        <div>
                          <span className="font-medium">Agent:</span> {r.assignedAgent}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex gap-2 mt-4">
                        <ViewButton
                          onClick={() =>
                            navigate(`/user/${r.id}`, {
                              state: { entityType: "retailer", data: r },
                            })
                          }
                        />

                        <button
                          onClick={() => openUpdateModal(r)}
                          className="flex-1 rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-xs font-semibold text-blue-700"
                        >
                          Update
                        </button>

                        <button
                          onClick={() => handleToggleRetailerStatus(r.id)}
                          className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-xs font-semibold text-slate-700"
                        >
                          {r.status === 1 ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    </div>
                  ))}

                  {!loading && totalPages > 1 && (
                  <div className="mt-4 flex justify-center md:justify-end">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
                </div>

                {/* Desktop Table */}
                <div className="hidden md:block">
                  <SimpleTable
                    headers={[
                      "Retailer",
                      "Contact",
                      "Location",
                      // "Agent",
                      "Created By",
                      "Status",
                    ]}
                    rows={retailerRows}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                    renderActions={(r) => (
                      <div className="flex items-center gap-2">
                        <ViewButton
                          onClick={() =>
                            navigate(`/user/${r.id}`, {
                              state: { entityType: "retailer", data: r },
                            })
                          }
                        />

                        <button
                          onClick={() => openUpdateModal(r)}
                          className="rounded-lg border border-blue-200 bg-blue-50 px-2 py-1 text-[10px] font-semibold text-blue-700"
                        >
                          Update
                        </button>

                        <button
                          onClick={() => handleToggleRetailerStatus(r.id)}
                          className="rounded-lg border border-slate-200 bg-slate-50 px-2 py-1 text-[10px] font-semibold text-slate-700"
                        >
                          {r.status === 1 ? "Deactivate" : "Activate"}
                        </button>
                      </div>
                    )}
                    showSrNo
                    pageSize={10}
                  />
                </div>
              </>
            )}
          </main>
        </SectionCard>

        {filterStatus !== "Connected" && (
          <SectionCard title="Retailer Usage Summary (Combined Portals)">
            <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
              <li>Ops dashboard: assignment, sales summary, retailer activity.</li>
              <li>Retailer portal: warranty, invoices, credit notes.</li>
              <li>Retailer user portal: tasks & data entry.</li>
              <li>Customer portal: download certificates & claims.</li>
            </ul>
          </SectionCard>
        )}

        {showUpdateModal && selectedRetailer && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-xl shadow-xl w-full max-w-5xl max-h-[90vh] overflow-y-auto">

              <h2 className="text-xl font-semibold mb-4">Update Retailer</h2>

              <UpdateRetailerForm
                retailer={selectedRetailer}
                onClose={closeUpdateModal}
                onSuccess={() => {
                  fetchRetailers();
                  closeUpdateModal();
                }}
              />
            </div>
          </div>
        )}
        </>
      )}

        {activeTab === "steps" && (
          <SectionCard title="Retailer Onboarding Progress" 
          right={
            <div className="flex items-center gap-2 text-[11px]">
              <div className="flex items-center gap-1 px-2 py-1 rounded-xl border border-slate-200 bg-slate-50">
                <Search className="w-5 h-5" />
                <input
                  placeholder="Search retailer..."
                  className="bg-transparent text-[11px] outline-none"
                  value={search}
                  onChange={(e) => searchRetailers(e.target.value)}
                />
              </div>

              {/* <BlueButton
                onClick={() => navigate("/add-entity/retailer")}
                className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
                style={{ backgroundColor: "#0B5ED7" }}
              >
                <Plus size={14} className="mr-1" /> Add
              </BlueButton> */}

              <div className="hidden lg:block">
                <RefreshButton onClick={() => fetchRetailers(currentPage)} />
              </div>
            </div>
          }
          >


            {loading && <RegularLoader />}

            {!loading && (
              <>
                <div className="overflow-x-auto">
                  <table className="min-w-full text-sm text-left border border-slate-200 rounded-lg">
                    
                    <thead className="bg-slate-50 border-b">
                      <tr >
                        <th className="px-4 py-2">Retailer</th>
                        <th className="px-4 py-2 text-center">Mail + WA</th>
                        <th className="px-4 py-2 text-center">Payment</th>
                        <th className="px-4 py-2 text-center">PAN</th>
                        <th className="px-4 py-2 text-center">GST</th>
                        <th className="px-4 py-2 text-center">Bank</th>
                        <th className="px-4 py-2 text-center">agreement</th>
                        <th className="px-4 py-2 text-center">E-Sign</th>
                      </tr>
                    </thead>

                    <tbody>
                      {paginatedRetailers.map((r) => {
                        const steps = getRetailerSteps(r.raw);

                        return (
                          <tr
                            key={r.id}
                            className="border-b hover:bg-slate-50 cursor-pointer"
                            onClick={() =>
                            navigate(`/user/${r.id}`, {
                              state: { entityType: "retailer", data: r },
                            })
                          }
                          >
                            {/* Retailer */}
                            <td className="px-4 py-2">
                              <div className="font-semibold">
                                {r.name}
                              </div>
                              <div className="text-xs text-slate-500">
                                {r.company_code}
                              </div>
                            </td>

                            {/* Steps */}
                            {steps.map((step) => (
                              <td
                                key={step.label}
                                className="px-4 py-2 text-center"
                              >
                                {step.done ? (
                                  <CheckCircle
                                    size={18}
                                    className="text-green-600 mx-auto"
                                  />
                                ) : (
                                  <Clock
                                    size={18}
                                    className="text-amber-500 mx-auto"
                                  />
                                )}
                              </td>
                            ))}
                          </tr>
                        );
                      })}
                    </tbody>

                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-4 flex justify-end">
                    <Pagination
                      currentPage={currentPage}
                      totalPages={totalPages}
                      onPageChange={setCurrentPage}
                    />
                  </div>
                )}
              </>
            )}
          </SectionCard>
        )}

      </div>
    </div>
  );
}



// import { Filter, Plus, Search } from "lucide-react";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
// import { useEffect, useState } from "react";
// import { useLocation, useNavigate } from "react-router-dom";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";

// // 👉 STATIC fallback values (kept for KPIs and usage UI)
// const fallbackRetailerData = [
//   {
//     id: 0, // will be overridden by API
//     totalSales: 0,
//     totalWarrantySold: 0,
//     activeWarranties: 0,
//     deactiveWarranties: 0,
//     pendingTasks: 0,
//     assignedAgent: "Not Assigned",
//     connectionStatus: "Connected",
//     usingProduct: true,
//     status: "Active",
//   }
// ];

// export default function OperationsView() {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const filterStatus = location.state?.filterStatus || null;

//   const [retailerList, setRetailerList] = useState([]);

//   // 🔥 Fetch company_id from session
//   const getCompanyId = () => {
//     const session = localStorage.getItem("authUserData");
//     if (!session) return 1;
//     try {
//       return JSON.parse(session).id || 1;
//     } catch {
//       return 1;
//     }
//   };

//   // 🔥 Fetch retailers from API + merge fallback fields
//   const fetchRetailers = async () => {
//     try {
//       const companyId = getCompanyId();

//       const res = await fetch("${BASE_URL}/retailer/all", {
//         method: "POST",
//         headers: { "Content-Type": "application/json" },
//         body: JSON.stringify({ company_id: companyId }),
//       });

//       const data = await res.json();

//       if (data.status === true && Array.isArray(data.data)) {
//         // merge API + fallback data
//         const merged = data.data.map((r) => ({
//           ...fallbackRetailerData[0], // add default KPI fields
//           id: r.id,
//           name: r.business_name,
//           code: r.retailer_code,
//           city: r.city,
//           state: r.state,
//           gstin: r.gst_number,
//           mobile: r.phone,
//           assignedAgent: fallbackRetailerData[0].assignedAgent,
//           connectionStatus: fallbackRetailerData[0].connectionStatus,
//           usingProduct: fallbackRetailerData[0].usingProduct,
//           totalSales: fallbackRetailerData[0].totalSales,
//           totalWarrantySold: fallbackRetailerData[0].totalWarrantySold,
//           activeWarranties: fallbackRetailerData[0].activeWarranties,
//           deactiveWarranties: fallbackRetailerData[0].deactiveWarranties,
//           pendingTasks: fallbackRetailerData[0].pendingTasks,
//           status: r.status === 1 ? "Active" : "Deactivated",
//         }));

//         setRetailerList(merged);
//       }
//     } catch (err) {
//       console.error("Retailer Fetch Error:", err);
//     }
//   };

//   // load API on page open
//   useEffect(() => {
//     fetchRetailers();
//   }, []);

//   // Apply filter from KPI card
//   useEffect(() => {
//     if (!filterStatus) return;

//     if (filterStatus === "Connected") {
//       setRetailerList((prev) =>
//         prev.filter((r) => r.connectionStatus === "Connected")
//       );
//     } else {
//       fetchRetailers();
//     }
//   }, [filterStatus]);

//   const handleToggleRetailerStatus = (id) => {
//     setRetailerList((prev) =>
//       prev.map((r) =>
//         r.id === id
//           ? { ...r, status: r.status === "Active" ? "Deactivated" : "Active" }
//           : r
//       )
//     );
//   };

//   return (
//     <div className="min-h-screen flex flex-col">
//       <div className="space-y-4">

//         <SectionCard
//           title={
//             filterStatus === "Connected"
//               ? "Connected Retailers"
//               : "Retailers by Status & Usage"
//           }
//           right={
//             <div className="flex items-center gap-2 text-[11px]">
//               <div className="hidden md:flex items-center gap-1 px-2 py-1 rounded-xl border border-slate-200 bg-slate-50">
//                 <Search className="w-3 h-3" />
//                 <span>Search retailer...</span>
//               </div>
//               {/* <button className="flex items-center gap-1 px-2 py-1 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100">
//                 <Filter className="w-3 h-3" />
//                 <span>Filter</span>
//               </button> */}

//               <BlueButton
//                 onClick={() => navigate("/add-entity/retailer")}
//                 className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//                 style={{ backgroundColor: "#0B5ED7" }}
//               >
//                 <Plus size={14} className="mr-1" /> Add
//               </BlueButton>

//               <RefreshButton onClick={fetchRetailers} />
//             </div>
//           }
//         >
//           {/* TABLE */}
//           <main className="flex-1">
//             <div className="overflow-x-auto">
//               <table className="min-w-full text-xs">
//                 <thead>
//                   <tr className="border-b border-slate-100 text-slate-500">
//                     <th className="py-2 text-left">Retailer</th>
//                     <th className="py-2 text-left">Contact</th>
//                     <th className="py-2 text-left">Agent</th>
//                     <th className="py-2 text-left">Status</th>
//                     <th className="py-2 text-left">Usage</th>
//                     <th className="py-2 text-left">Warranties</th>
//                     <th className="py-2 text-left">Tasks</th>
//                     <th className="py-2 text-left">Actions</th>
//                   </tr>
//                 </thead>

//                 <tbody>
//                   {retailerList.map((r) => (
//                     <tr
//                       key={r.id}
//                       className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                     >
//                       <td className="py-1.5 text-xs text-slate-700">
//                         <div className="font-semibold">{r.name}</div>
//                         <div className="text-[10px] text-slate-500">
//                           {r.code} · {r.city}, {r.state}
//                         </div>
//                         <div className="text-[10px] text-slate-400">
//                           GSTIN: {r.gstin}
//                         </div>
//                       </td>

//                       <td className="py-1.5 text-[11px] text-slate-700">
//                         <div>{r.mobile}</div>
//                         <div className="text-[10px] text-slate-500">
//                           Click-to-call (CTI placeholder)
//                         </div>
//                       </td>

//                       <td className="py-1.5 text-xs text-slate-700">
//                         <div>{r.assignedAgent}</div>
//                         <div className="text-[10px] text-slate-500">
//                           Primary owner
//                         </div>
//                       </td>

//                       <td className="py-1.5 text-xs">
//                         <div className="flex flex-col gap-1">
//                           <Badge color={r.status === "Active" ? "green" : "gray"}>
//                             {r.status}
//                           </Badge>

//                           <Badge
//                             color={
//                               r.connectionStatus === "Connected" ? "blue" : "yellow"
//                             }
//                           >
//                             {r.connectionStatus === "Connected"
//                               ? "Connected to platform"
//                               : "Not connected"}
//                           </Badge>
//                         </div>
//                       </td>

//                       <td className="py-1.5 text-[11px] text-slate-700">
//                         <div>Sales: ₹ {formatCurrency(r.totalSales)}</div>
//                         <div className="text-[10px] text-slate-500">
//                           Using product: {r.usingProduct ? "Yes" : "No"}
//                         </div>
//                       </td>

//                       <td className="py-1.5 text-[11px] text-slate-700">
//                         <div>Total: {r.totalWarrantySold}</div>
//                         <div className="text-[10px] text-slate-500">
//                           Active: {r.activeWarranties} · Expired: {r.deactiveWarranties}
//                         </div>
//                       </td>

//                       <td className="py-1.5 text-[11px] text-slate-700">
//                         <div>Open tasks: {r.pendingTasks}</div>
//                         <button className="mt-1 text-[10px] px-2 py-0.5 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100">
//                           Create task
//                         </button>
//                       </td>

//                       <td className="py-1.5 text-[11px] text-slate-700">
//                         <button
//                           onClick={() => handleToggleRetailerStatus(r.id)}
//                           className="text-[10px] px-2 py-0.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100"
//                         >
//                           {r.status === "Active" ? "Deactivate" : "Activate"}
//                         </button>
//                       </td>
//                     </tr>
//                   ))}
//                 </tbody>
//               </table>
//             </div>
//           </main>
//         </SectionCard>

//         {filterStatus !== "Connected" && (
//           <SectionCard title="Retailer Usage Summary (Combined Portals)">
//             <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
//               <li>Ops dashboard: assignment, sales summary, retailer activity.</li>
//               <li>Retailer portal: warranty, invoices, credit notes.</li>
//               <li>Retailer user portal: tasks & data entry.</li>
//               <li>Customer portal: download certificates & claims.</li>
//             </ul>
//           </SectionCard>
//         )}
//       </div>
//     </div>
//   );
// }




// import { Filter, Plus, Search } from "lucide-react";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
// import { useEffect, useState } from "react";
// import { useLocation, useNavigate } from "react-router-dom";
// import BlueButton from "../../../Component/Common/BlueButton";
// import RefreshButton from "../../../Component/Common/RefreshButton";


// const retailers = [
//   {
//     id: 1,
//     name: "Shree Mobile Store",
//     code: "RET-001",
//     city: "Pune",
//     state: "Maharashtra",
//     gstin: "27ABCDE1234Z5Z6",
//     mobile: "+91 98765 11111",
//     assignedAgent: "Rahul Patil",
//     status: "Active",
//     connectionStatus: "Connected",
//     usingProduct: true,
//     totalSales: 480000,
//     totalWarrantySold: 56,
//     activeWarranties: 48,
//     deactiveWarranties: 8,
//     pendingTasks: 2,
//     activationsThisMonth: 8,
//   },
//   {
//     id: 2,
//     name: "A1 Electronics",
//     code: "RET-002",
//     city: "Mumbai",
//     state: "Maharashtra",
//     gstin: "27PQRSX5678L9A1",
//     mobile: "+91 99887 22222",
//     assignedAgent: "Priya Singh",
//     status: "Active",
//     connectionStatus: "Not Connected",
//     usingProduct: false,
//     totalSales: 120000,
//     totalWarrantySold: 15,
//     activeWarranties: 13,
//     deactiveWarranties: 2,
//     pendingTasks: 1,
//     activationsThisMonth: 3,
//   },
//   {
//     id: 3,
//     name: "Digital Zone",
//     code: "RET-003",
//     city: "Nashik",
//     state: "Maharashtra",
//     gstin: "27LMNOP9999C3F2",
//     mobile: "+91 91234 55555",
//     assignedAgent: "Rahul Patil",
//     status: "Deactivated",
//     connectionStatus: "Connected",
//     usingProduct: false,
//     totalSales: 80000,
//     totalWarrantySold: 9,
//     activeWarranties: 3,
//     deactiveWarranties: 6,
//     pendingTasks: 0,
//     activationsThisMonth: 0,
//   },
// ];

// export default function OperationsView() {
//   const navigate = useNavigate();
//   const location = useLocation();
//   const filterStatus = location.state?.filterStatus || null;

//   const [retailerList, setRetailerList] = useState(retailers);

//   // 🔥 APPLY FILTER WHEN COMING FROM KPI CARD
//   useEffect(() => {
//     if (filterStatus === "Connected") {
//       setRetailerList(retailers.filter((r) => r.connectionStatus === "Connected"));
//     } else {
//       setRetailerList(retailers); // default: show all
//     }
//   }, [filterStatus]);

//   const handleToggleRetailerStatus = (id) => {
//     setRetailerList((prev) =>
//       prev.map((r) =>
//         r.id === id
//           ? { ...r, status: r.status === "Active" ? "Deactivated" : "Active" }
//           : r
//       )
//     );
//   };

//   return (
//     <div className="min-h-screen flex flex-col">
//     <div className="space-y-4">

//       <SectionCard
//         title={
//           filterStatus === "Connected"
//             ? "Connected Retailers"
//             : "Retailers by Status & Usage"
//         }
//         right={
//           <div className="flex items-center gap-2 text-[11px]">
//             <div className="hidden md:flex items-center gap-1 px-2 py-1 rounded-xl border border-slate-200 bg-slate-50">
//               <Search className="w-3 h-3" />
//               <span>Search retailer...</span>
//             </div>
//             <button className="flex items-center gap-1 px-2 py-1 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100">
//               <Filter className="w-3 h-3" />
//               <span>Filter</span>
//             </button>

//             <BlueButton
//               onClick={() => navigate("/add-entity/retailer")} // or "company", "retailer"
//               className="flex items-center justify-center rounded-lg px-3 py-2 text-sm font-semibold text-white"
//               style={{ backgroundColor: "#0B5ED7" }}
//             >
//               <Plus size={14} className="mr-1" /> Add
//             </BlueButton>
//             <RefreshButton/>
//           </div>
//         }
//       >
//         <main className="flex-1">
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b border-slate-100 text-slate-500">
//                 <th className="py-2 text-left">Retailer</th>
//                 <th className="py-2 text-left">Contact</th>
//                 <th className="py-2 text-left">Agent</th>
//                 <th className="py-2 text-left">Status</th>
//                 <th className="py-2 text-left">Usage</th>
//                 <th className="py-2 text-left">Warranties</th>
//                 <th className="py-2 text-left">Tasks</th>
//                 <th className="py-2 text-left">Actions</th>
//               </tr>
//             </thead>

//             <tbody>
//               {retailerList.map((r) => (
//                 <tr
//                   key={r.id}
//                   className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                 >
//                   <td className="py-1.5 text-xs text-slate-700">
//                     <div className="font-semibold">{r.name}</div>
//                     <div className="text-[10px] text-slate-500">
//                       {r.code} · {r.city}, {r.state}
//                     </div>
//                     <div className="text-[10px] text-slate-400">GSTIN: {r.gstin}</div>
//                   </td>

//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>{r.mobile}</div>
//                     <div className="text-[10px] text-slate-500">
//                       Click-to-call (CTI placeholder)
//                     </div>
//                   </td>

//                   <td className="py-1.5 text-xs text-slate-700">
//                     <div>{r.assignedAgent}</div>
//                     <div className="text-[10px] text-slate-500">Primary owner</div>
//                   </td>

//                   <td className="py-1.5 text-xs">
//                     <div className="flex flex-col gap-1">
//                       <Badge color={r.status === "Active" ? "green" : "gray"}>
//                         {r.status}
//                       </Badge>

//                       <Badge
//                         color={r.connectionStatus === "Connected" ? "blue" : "yellow"}
//                       >
//                         {r.connectionStatus === "Connected"
//                           ? "Connected to platform"
//                           : "Not connected"}
//                       </Badge>
//                     </div>
//                   </td>

//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>Sales: ₹ {formatCurrency(r.totalSales)}</div>
//                     <div className="text-[10px] text-slate-500">
//                       Using product: {r.usingProduct ? "Yes" : "No"}
//                     </div>
//                   </td>

//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>Total: {r.totalWarrantySold}</div>
//                     <div className="text-[10px] text-slate-500">
//                       Active: {r.activeWarranties} · Expired: {r.deactiveWarranties}
//                     </div>
//                   </td>

//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>Open tasks: {r.pendingTasks}</div>
//                     <button className="mt-1 text-[10px] px-2 py-0.5 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100">
//                       Create task
//                     </button>
//                   </td>

//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <button
//                       onClick={() => handleToggleRetailerStatus(r.id)}
//                       className="text-[10px] px-2 py-0.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100"
//                     >
//                       {r.status === "Active" ? "Deactivate" : "Activate"}
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//         </main>
//       </SectionCard>

//       {/* SUMMARY */}
//       {filterStatus !== "Connected" && (
//         <div className="min-h-screen flex flex-col">
//         <SectionCard title="Retailer Usage Summary (Combined Portals)">
//           <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
//             <li>Ops dashboard: assignment, sales summary, retailer activity.</li>
//             <li>Retailer portal: warranty, invoices, credit notes.</li>
//             <li>Retailer user portal: tasks & data entry.</li>
//             <li>Customer portal: download certificates & claims.</li>
//           </ul>
//         </SectionCard>
//         </div>
//       )}
//     </div>
//     </div>
//   );
// }



// import { Filter, Search } from "lucide-react";
// import { SectionCard } from "../../../Component/AdminComponents/SectionCard";
// import { Badge } from "../../../Component/AdminComponents/Badge";
// import { formatCurrency } from "../../../Component/AdminComponents/formatCurrency";
// import { useState } from "react";

// const retailers = [
//   {
//     id: 1,
//     name: "Shree Mobile Store",
//     code: "RET-001",
//     city: "Pune",
//     state: "Maharashtra",
//     gstin: "27ABCDE1234Z5Z6",
//     mobile: "+91 98765 11111",
//     assignedAgent: "Rahul Patil",
//     status: "Active", // Active | Deactivated
//     connectionStatus: "Connected", // Connected | Not Connected
//     usingProduct: true,
//     totalSales: 480000,
//     totalWarrantySold: 56,
//     activeWarranties: 48,
//     deactiveWarranties: 8,
//     pendingTasks: 2,
//     activationsThisMonth: 8,
//   },
//   {
//     id: 2,
//     name: "A1 Electronics",
//     code: "RET-002",
//     city: "Mumbai",
//     state: "Maharashtra",
//     gstin: "27PQRSX5678L9A1",
//     mobile: "+91 99887 22222",
//     assignedAgent: "Priya Singh",
//     status: "Active",
//     connectionStatus: "Not Connected",
//     usingProduct: false,
//     totalSales: 120000,
//     totalWarrantySold: 15,
//     activeWarranties: 13,
//     deactiveWarranties: 2,
//     pendingTasks: 1,
//     activationsThisMonth: 3,
//   },
//   {
//     id: 3,
//     name: "Digital Zone",
//     code: "RET-003",
//     city: "Nashik",
//     state: "Maharashtra",
//     gstin: "27LMNOP9999C3F2",
//     mobile: "+91 91234 55555",
//     assignedAgent: "Rahul Patil",
//     status: "Deactivated",
//     connectionStatus: "Connected",
//     usingProduct: false,
//     totalSales: 80000,
//     totalWarrantySold: 9,
//     activeWarranties: 3,
//     deactiveWarranties: 6,
//     pendingTasks: 0,
//     activationsThisMonth: 0,
//   },
// ];

// export default function OperationsView() {

//   const [retailerList, setRetailerList] = useState(retailers);

//   const handleToggleRetailerStatus = (id) => {
//     setRetailerList((prev) =>
//       prev.map((r) =>
//         r.id === id
//           ? {
//               ...r,
//               status: r.status === "Active" ? "Deactivated" : "Active",
//             }
//           : r
//       )
//     );
//   };
//   return (
//     <div className="space-y-4">
//       <SectionCard
//         title="Retailers by Status & Usage"
//         right={
//           <div className="flex items-center gap-2 text-[11px]">
//             <div className="hidden md:flex items-center gap-1 px-2 py-1 rounded-xl border border-slate-200 bg-slate-50">
//               <Search className="w-3 h-3" />
//               <span>Search retailer...</span>
//             </div>
//             <button className="flex items-center gap-1 px-2 py-1 rounded-xl border border-slate-200 bg-slate-50 text-slate-600 hover:bg-slate-100">
//               <Filter className="w-3 h-3" />
//               <span>Filter</span>
//             </button>
//           </div>
//         }
//       >
//         <div className="overflow-x-auto">
//           <table className="min-w-full text-xs">
//             <thead>
//               <tr className="border-b border-slate-100 text-slate-500">
//                 <th className="py-2 text-left">Retailer</th>
//                 <th className="py-2 text-left">Contact</th>
//                 <th className="py-2 text-left">Agent</th>
//                 <th className="py-2 text-left">Status</th>
//                 <th className="py-2 text-left">Usage</th>
//                 <th className="py-2 text-left">Warranties</th>
//                 <th className="py-2 text-left">Tasks</th>
//                 <th className="py-2 text-left">Actions</th>
//               </tr>
//             </thead>
//             <tbody>
//               {retailerList.map((r) => (
//                 <tr
//                   key={r.id}
//                   className="border-b border-slate-50 bg-white hover:bg-blue-50/60"
//                 >
//                   <td className="py-1.5 text-xs text-slate-700">
//                     <div className="font-semibold">{r.name}</div>
//                     <div className="text-[10px] text-slate-500">
//                       {r.code} · {r.city}, {r.state}
//                     </div>
//                     <div className="text-[10px] text-slate-400">GSTIN: {r.gstin}</div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>{r.mobile}</div>
//                     <div className="text-[10px] text-slate-500">
//                       Click-to-call from Ops dashboard (CTI integration placeholder)
//                     </div>
//                   </td>
//                   <td className="py-1.5 text-xs text-slate-700">
//                     <div>{r.assignedAgent}</div>
//                     <div className="text-[10px] text-slate-500">Primary owner</div>
//                   </td>
//                   <td className="py-1.5 text-xs">
//                     <div className="flex flex-col gap-1">
//                       <Badge color={r.status === "Active" ? "green" : "gray"}>
//                         {r.status}
//                       </Badge>
//                       <Badge
//                         color={
//                           r.connectionStatus === "Connected" ? "blue" : "yellow"
//                         }
//                       >
//                         {r.connectionStatus === "Connected"
//                           ? "Connected to platform"
//                           : "Not connected"}
//                       </Badge>
//                     </div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>Sales: ₹ {formatCurrency(r.totalSales)}</div>
//                     <div className="text-[10px] text-slate-500">
//                       Using product: {r.usingProduct ? "Yes" : "No"}
//                     </div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>Total: {r.totalWarrantySold}</div>
//                     <div className="text-[10px] text-slate-500">
//                       Active: {r.activeWarranties} · Expired: {r.deactiveWarranties}
//                     </div>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <div>Open tasks: {r.pendingTasks}</div>
//                     <button className="mt-1 text-[10px] px-2 py-0.5 rounded-lg border border-blue-200 text-blue-700 bg-blue-50 hover:bg-blue-100">
//                       Create task
//                     </button>
//                   </td>
//                   <td className="py-1.5 text-[11px] text-slate-700">
//                     <button
//                       onClick={() => handleToggleRetailerStatus(r.id)}
//                       className="text-[10px] px-2 py-0.5 rounded-lg border border-slate-200 bg-slate-50 hover:bg-slate-100"
//                     >
//                       {r.status === "Active" ? "Deactivate" : "Activate"}
//                     </button>
//                   </td>
//                 </tr>
//               ))}
//             </tbody>
//           </table>
//         </div>
//       </SectionCard>

//       <SectionCard title="Retailer Usage Summary (Combined Portals)">
//         <ul className="list-disc list-inside text-xs text-slate-600 space-y-1">
//           <li>
//             Operation dashboard: assignment, sales summary, active vs deactivated
//             retailers, connection & product usage.
//           </li>
//           <li>
//             Retailer portal (global): warranty sale, invoices, uninvoiced amount,
//             credit notes, payables & earnings.
//           </li>
//           <li>
//             Retailer user portal: per-user access for warranty entry, view &
//             legal/consent pages.
//           </li>
//           <li>
//             Customer warranty portal: customer downloads certificates, sees coverage
//             and raises claim.
//           </li>
//         </ul>
//       </SectionCard>
//     </div>
//   );
// }